import React, {
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useSwipeable } from 'react-swipeable';
import { gsap } from 'gsap';

import * as imagePreviewActions from '@/components/modals/imagePreview/Actions';
import * as playlistActions from '@/playlist/Actions';
import { FRONTEND_ROUTES } from '@/services/Constants';
import { SCREEN_SIZES } from '@/shared/Constants';
import { getFreePlayCategoryPath } from '@/pages/freePlay/Helpers';
import {
	computeBottomLaneCenters,
	getLaneBottomPct,
	getLaneTopPct,
	preventMobileHistoryNavigation,
	supportsTouch,
} from '@/playlist/games/laneChooser/Helpers';
import { isSpaceKey } from '@/shared/Helpers';
import useGameLayout from '@/playlist/games/common/hooks/useGameLayout';
import useCharacterMovement from '@/playlist/games/laneChooser/hooks/useCharacterMovement';
import useCountdown from '@/playlist/games/laneChooser/hooks/useCountdown';
import useLaneChooserQuestion from '@/playlist/games/laneChooser/hooks/useLaneChooserQuestion';
import useAudioPlayer from '@/services/hocs/useAudioPlayer';
import { LaneChooserThemeProvider, useLaneChooserTheme } from '@/playlist/games/laneChooser/theme';
import PlaylistExitContext from '@/playlist/PlaylistExitContext';
import { EndOutcome, PlayList, Question } from '@/playlist/types';
import { historyPush } from '@/services/History';

import AnswersPanel, {
	AnswersPanelHandle,
} from '@/playlist/games/laneChooser/components/answers/AnswersPanel';
import Character from '@/playlist/games/laneChooser/components/elements/Character';
import KeyInstructions, {
	KeyInstructionsHandle,
} from '@/playlist/games/laneChooser/components/elements/KeyInstructions';
import Sky from '@/playlist/games/laneChooser/components/elements/Sky';
import EndScreen from '@/playlist/games/laneChooser/components/EndScreen';
import LaneLayer from '@/playlist/games/laneChooser/components/lane/LaneLayer';
import SideElements from '@/playlist/games/laneChooser/components/lane/SideElements';
import Progress from '@/playlist/games/laneChooser/components/progress/Progress';
import QuestionPanel from '@/playlist/games/laneChooser/components/QuestionPanel';
import StartScreen from '@/playlist/games/laneChooser/components/StartScreen';

import boostSfx from '@/playlist/games/laneChooser/assets/sounds/boost.mp3';
import falseAnswerSfx from '@/playlist/games/laneChooser/assets/sounds/falseanswer.mp3';
import goodAnswerSfx from '@/playlist/games/laneChooser/assets/sounds/goodanswer.mp3';
import jumpSfx from '@/playlist/games/laneChooser/assets/sounds/jump.mp3';
import slowdownSfx from '@/playlist/games/laneChooser/assets/sounds/slowdown.mp3';
import surfingSpeedwayGame from '@/playlist/games/laneChooser/assets/sounds/surfing-speedway-game.mp3';
import switchLaneLeftSfx from '@/playlist/games/laneChooser/assets/sounds/switchlane-left.mp3';
import switchLaneRightSfx from '@/playlist/games/laneChooser/assets/sounds/switchlane-right.mp3';

import '@/playlist/games/laneChooser/styles/lane-chooser.scss';
import '@/playlist/games/laneChooser/styles/lane-chooser-themes.scss';

type LaneChooserGameProps = {
	playList: PlayList;
	isBridgePlaylist: boolean;
	sendAnswerRequest: typeof playlistActions.sendAnswerRequest;
	finishPlaylistRequest: typeof playlistActions.finishPlaylistRequest;
	questions: Question[];
	setImagePreviewModal: typeof imagePreviewActions.setImagePreviewModal;
};

const LaneChooserGameInner = ({
	playList,
	isBridgePlaylist,
	sendAnswerRequest,
	finishPlaylistRequest,
	questions,
	setImagePreviewModal,
}: LaneChooserGameProps) => {
	useGameLayout();

	const [gameStarted, setGameStarted] = useState<boolean>(false);
	const [showStartScreen, setShowStartScreen] = useState<boolean>(true);
	const [endOutcome, setEndOutcome] = useState<EndOutcome | null>(null);
	const [gameVisible, setGameVisible] = useState<boolean>(false);
	// Cumulative time adjustments (+/- seconds) applied to remaining time
	const [timeAdjustSeconds, setTimeAdjustSeconds] = useState<number>(0);
	// Track current streak of consecutive correct answers (resets on wrong)
	const [correctStreak, setCorrectStreak] = useState<number>(0);
	// Reward seconds for the last confirmed answer (to show in delta)
	const [lastDeltaSeconds, setLastDeltaSeconds] = useState<number | undefined>(undefined);
	// Track answers locally instead of in Redux
	const [answers, setAnswers] = useState<boolean[]>([]);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const answersRef = useRef<AnswersPanelHandle | null>(null);
	const laneLayerRef = useRef<HTMLDivElement | null>(null);
	const trackRef = useRef<HTMLDivElement | null>(null);
	const qRef = useRef<HTMLDivElement | null>(null);
	const questionContainerRef = useRef<HTMLDivElement | null>(null);
	const answersAlreadyAtBottomRef = useRef<boolean>(false);
	const isQuestionTransitioningRef = useRef<boolean>(false);
	const keyInstructionsRef = useRef<KeyInstructionsHandle | null>(null);

	const { game } = playList || {};
	const { categoryId: categoryIdFromRoute, category: categorySlugFromRoute } = useParams<{
		categoryId?: string;
		category?: string;
	}>();

	const freePlayCategoryPath = useMemo(() => {
		if (categoryIdFromRoute && categorySlugFromRoute) {
			return getFreePlayCategoryPath(categoryIdFromRoute, categorySlugFromRoute);
		}
		return undefined;
	}, [categoryIdFromRoute, categorySlugFromRoute]);

	const theme = useLaneChooserTheme();

	// Initialize streak when game info is received
	useEffect(() => {
		setCorrectStreak(Number(game?.correct_answer_streak || 0));
	}, [game?.correct_answer_streak]);

	const totalSeconds = useMemo(() => {
		return Number(game?.total_time_in_seconds || 0);
	}, [game]);

	// Lane handling moved to composable
	const [confirmedIndex, setConfirmedIndex] = useState<number | null>(null);
	const [jumping, setJumping] = useState<boolean>(false);
	const [celebratingCorrect, setCelebratingCorrect] = useState<boolean>(false);
	const [boosting, setBoosting] = useState<boolean>(false);
	const lastConfirmedWasCorrectRef = useRef<boolean | null>(null);
	const hasConfirmedCurrentQuestionRef = useRef<boolean>(false);
	const laneIndexRef = useRef<number>(0);

	const { play: playGameMusic, stop: stopGameMusic } = useAudioPlayer({
		src: surfingSpeedwayGame,
		loop: true,
		volume: 0.5,
	});
	const { play: playSwitchLaneLeft, stop: stopSwitchLaneLeft } = useAudioPlayer({
		src: switchLaneLeftSfx,
		volume: 0.7,
	});
	const { play: playSwitchLaneRight, stop: stopSwitchLaneRight } = useAudioPlayer({
		src: switchLaneRightSfx,
		volume: 0.7,
	});
	const { play: playBoostSfx, stop: stopBoostSfx } = useAudioPlayer({
		src: boostSfx,
		volume: 0.7,
	});
	const { play: playSlowdownSfx, stop: stopSlowdownSfx } = useAudioPlayer({
		src: slowdownSfx,
		volume: 0.7,
	});
	const { play: playJumpSfx, stop: stopJumpSfx } = useAudioPlayer({
		src: jumpSfx,
		volume: 0.7,
	});
	const { play: playGoodAnswer } = useAudioPlayer({
		src: goodAnswerSfx,
		volume: 0.7,
	});
	const { play: playFalseAnswer } = useAudioPlayer({
		src: falseAnswerSfx,
		volume: 0.7,
	});

	const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
	// Track when the question became active to compute answer_time_in_seconds
	const [questionStartTs, setQuestionStartTs] = useState<number>(Date.now());

	const currentQuestion = questions[currentQuestionIndex];
	const currentQuestionKey = currentQuestion?.id ?? currentQuestionIndex;

	const freePlayCategoryId = categoryIdFromRoute ? Number(categoryIdFromRoute) : undefined;

	const { details: questionDetails, submitAnswer } = useLaneChooserQuestion({
		question: currentQuestion,
		playList,
		questionStartTs,
		sendAnswerRequest,
		freePlayCategoryId,
	});

	const {
		answerLabels,
		answerCorrectFlags,
		imageUrls,
		questionHtml,
		questionHasImage,
		titleText,
	} = questionDetails;

	const handleClickQuestionImage = useCallback(
		(imageSrc: string) => {
			setImagePreviewModal({
				open: true,
				imageSrc,
			} as any);
		},
		[setImagePreviewModal],
	);

	const lanesCount = useMemo(
		() => Math.max(1, Math.min(4, answerLabels.length || 4)),
		[answerLabels],
	);

	const handleConfirmAnswer = useCallback((): boolean => {
		if (hasConfirmedCurrentQuestionRef.current) {
			return false;
		}
		const selected = laneIndexRef.current;
		setConfirmedIndex(selected);

		// Check if this is the last question and prepare finish callback
		const isLastQuestion = currentQuestionIndex >= questions.length - 1;
		const playlistId = playList?.id;
		const shouldFinish = isLastQuestion && playlistId != null;
		// In bridge playlist, sendAnswerRequest is skipped so the callback is never invoked.
		// Pass it only for regular playlists; bridge playlist triggers finishPlaylistRequest directly.
		const finishCallback =
			shouldFinish && !isBridgePlaylist
				? () => finishPlaylistRequest(playlistId, freePlayCategoryId, EndOutcome.SUCCEEDED)
				: undefined;

		const submission = submitAnswer(selected, finishCallback);
		if (!submission) {
			lastConfirmedWasCorrectRef.current = false;
			setLastDeltaSeconds(undefined);
			return false;
		}
		hasConfirmedCurrentQuestionRef.current = true;

		if (shouldFinish && isBridgePlaylist) {
			finishPlaylistRequest(playlistId, freePlayCategoryId, EndOutcome.SUCCEEDED);
		}

		const { isCorrect } = submission;
		setAnswers((prev) => [...prev, isCorrect]);

		let delta = 0;
		if (isCorrect) {
			playGoodAnswer();
			const nextCorrectStreak = correctStreak + 1;
			setCorrectStreak(nextCorrectStreak);
			delta = game?.reward?.correct_answer_streak?.[nextCorrectStreak] ?? 0;
			if (delta) {
				setTimeAdjustSeconds((prev) => prev + delta);
				setLastDeltaSeconds(delta);
			} else {
				setLastDeltaSeconds(undefined);
			}
		} else {
			playFalseAnswer();
			const nextWrongStreak = 1;
			setCorrectStreak(0);
			delta = game?.reward?.wrong_answer_streak?.[nextWrongStreak] ?? 0;
			setTimeAdjustSeconds((prev) => prev + delta);
			setLastDeltaSeconds(delta !== 0 ? delta : undefined);
		}
		lastConfirmedWasCorrectRef.current = isCorrect;
		return true;
	}, [
		correctStreak,
		game?.reward,
		playFalseAnswer,
		playGoodAnswer,
		submitAnswer,
		currentQuestionIndex,
		questions.length,
		playList?.id,
		freePlayCategoryId,
		isBridgePlaylist,
		finishPlaylistRequest,
	]);

	const BOOST_SCALE = 9;
	const NORMAL_SCALE = 1.6;

	const [motionScale, setMotionScale] = useState<number>(NORMAL_SCALE);
	const [motionDirection, setMotionDirection] = useState<1 | -1>(1);
	const [arrowUpHeld, setArrowUpHeld] = useState<boolean>(false);
	const [arrowDownHeld, setArrowDownHeld] = useState<boolean>(false);
	const [prefersSwipe, setPrefersSwipe] = useState<boolean>(() => {
		if (typeof window === 'undefined') return false;
		return window.innerWidth < SCREEN_SIZES.TABLET || supportsTouch();
	});
	const [isCompactWidth, setIsCompactWidth] = useState<boolean>(() => {
		if (typeof window === 'undefined') return false;
		return window.innerWidth < SCREEN_SIZES.MOBILE;
	});
	const [cameraTranslate, setCameraTranslate] = useState<number>(0);
	const arrowUpHeldRef = useRef<boolean>(false);
	const arrowDownHeldRef = useRef<boolean>(false);
	const swipeActiveDirectionRef = useRef<'forward' | 'reverse' | null>(null);

	const [bottomTargetY, setBottomTargetY] = useState<number | undefined>(undefined);
	const [answersAtBottom, setAnswersAtBottom] = useState<boolean>(false);

	const [showActionHint, setShowActionHint] = useState<boolean>(false);
	const actionHintTimerRef = useRef<number | null>(null);
	const clearActionHintTimer = useCallback(() => {
		if (actionHintTimerRef.current != null) {
			window.clearTimeout(actionHintTimerRef.current);
			actionHintTimerRef.current = null;
		}
	}, []);

	const applyMotion = useCallback((scale: number, direction: 1 | -1) => {
		setMotionScale(scale);
		setMotionDirection(direction);
	}, []);

	const { laneIndex, setLaneIndex, moveToLane, jump } = useCharacterMovement({
		answersAtBottom,
		qRef,
		laneLayerRef,
		lanes: lanesCount,
		laneBottomPct: getLaneBottomPct(),
		onJumpStart: () => setJumping(true),
		onJumpEnd: () => {
			setJumping(false);
			if (answersAtBottom) {
				setAnswersAtBottom(false);
			}
			if (lastConfirmedWasCorrectRef.current) {
				setCelebratingCorrect(true);
				setTimeout(() => {
					setCelebratingCorrect(false);
				}, 1500);
			}
		},
	});

	const moveCharacterLeft = useCallback(() => {
		if (laneIndexRef.current <= 0) return;
		stopSwitchLaneLeft();
		playSwitchLaneLeft();
		setLaneIndex((prev) => Math.max(0, prev - 1));
	}, [playSwitchLaneLeft, setLaneIndex, stopSwitchLaneLeft]);

	const moveCharacterRight = useCallback(() => {
		if (laneIndexRef.current >= lanesCount - 1) return;
		stopSwitchLaneRight();
		playSwitchLaneRight();
		setLaneIndex((prev) => Math.min(lanesCount - 1, prev + 1));
	}, [lanesCount, playSwitchLaneRight, setLaneIndex, stopSwitchLaneRight]);

	useEffect(() => {
		arrowUpHeldRef.current = arrowUpHeld;
	}, [arrowUpHeld]);

	useEffect(() => {
		arrowDownHeldRef.current = arrowDownHeld;
	}, [arrowDownHeld]);

	const startForwardBoost = useCallback(() => {
		if (answersAtBottom || arrowUpHeldRef.current) return;
		stopBoostSfx();
		playBoostSfx();
		arrowUpHeldRef.current = true;
		setArrowUpHeld(true);
		setBoosting(true);
		applyMotion(BOOST_SCALE, 1);
	}, [answersAtBottom, applyMotion, playBoostSfx, stopBoostSfx]);

	const stopForwardBoost = useCallback(() => {
		if (!arrowUpHeldRef.current) return;
		stopBoostSfx();
		arrowUpHeldRef.current = false;
		setArrowUpHeld(false);
		setBoosting(false);
		if (arrowDownHeldRef.current) {
			applyMotion(BOOST_SCALE, -1);
		} else {
			applyMotion(NORMAL_SCALE, 1);
		}
	}, [applyMotion, stopBoostSfx]);

	const startReverseBoost = useCallback(() => {
		if (answersAtBottom || arrowDownHeldRef.current) return;
		stopSlowdownSfx();
		playSlowdownSfx();
		arrowDownHeldRef.current = true;
		setArrowDownHeld(true);
		setBoosting(true);
		applyMotion(BOOST_SCALE, -1);
	}, [answersAtBottom, applyMotion, playSlowdownSfx, stopSlowdownSfx]);

	const stopReverseBoost = useCallback(() => {
		if (!arrowDownHeldRef.current) return;
		stopSlowdownSfx();
		arrowDownHeldRef.current = false;
		setArrowDownHeld(false);
		if (arrowUpHeldRef.current) {
			applyMotion(BOOST_SCALE, 1);
		} else {
			applyMotion(NORMAL_SCALE, 1);
			setBoosting(false);
		}
	}, [applyMotion, stopSlowdownSfx]);

	const handleSpaceKeyPress = useCallback(() => {
		stopJumpSfx();
		playJumpSfx();
		jump();
		setShowActionHint(false);
		clearActionHintTimer();
		if (answersAtBottom) {
			const confirmed = handleConfirmAnswer();
			if (confirmed) {
				answersRef.current?.skipToNext();
			}
		}
	}, [
		answersAtBottom,
		handleConfirmAnswer,
		clearActionHintTimer,
		jump,
		playJumpSfx,
		stopJumpSfx,
	]);

	const { display: countdownDisplay, start: startCountdown } = useCountdown({
		initial: 3,
		onFinish: () => {
			setGameStarted(true);
		},
		startDelayAfterEndMs: 500,
	});

	// Reset question timer when game actually starts
	useEffect(() => {
		if (gameStarted) setQuestionStartTs(Date.now());
	}, [gameStarted]);

	useEffect(() => {
		laneIndexRef.current = laneIndex;
	}, [laneIndex]);

	useEffect(() => {
		if (typeof window === 'undefined') return undefined;
		const updateResponsiveFlags = () => {
			const width = window.innerWidth;
			const prefers = width < SCREEN_SIZES.TABLET || supportsTouch();
			setPrefersSwipe(prefers);
			setIsCompactWidth(width < SCREEN_SIZES.MOBILE);
		};
		updateResponsiveFlags();
		window.addEventListener('resize', updateResponsiveFlags);
		return () => window.removeEventListener('resize', updateResponsiveFlags);
	}, []);

	const MOBILE_TRACK_SCALE = 2;
	const laneScale = isCompactWidth ? MOBILE_TRACK_SCALE : 1;
	const trackStyle: React.CSSProperties | undefined = isCompactWidth
		? {
				width: `${laneScale * 100}%`,
				transform: `translateX(${cameraTranslate}px)`,
		  }
		: undefined;

	const updateCamera = useCallback(() => {
		if (!isCompactWidth) {
			setCameraTranslate(0);
			return;
		}
		const viewportNode = containerRef.current;
		const laneNode = laneLayerRef.current;
		if (!viewportNode || !laneNode) return;
		const viewportWidth = viewportNode.clientWidth;
		const laneWidth = laneNode.clientWidth;
		if (viewportWidth <= 0 || laneWidth <= 0) return;
		const centers = computeBottomLaneCenters({
			width: laneWidth,
			lanes: lanesCount,
			laneBottomPct: getLaneBottomPct(),
		});
		const boundedIndex = Math.max(0, Math.min(laneIndex, centers.length - 1));
		const center = centers[boundedIndex] ?? laneWidth / 2;
		const halfViewport = viewportWidth / 2;
		const maxStart = Math.max(0, laneWidth - viewportWidth);
		let visibleStart = center - halfViewport;
		if (!Number.isFinite(visibleStart)) visibleStart = 0;
		if (visibleStart < 0) visibleStart = 0;
		if (visibleStart > maxStart) visibleStart = maxStart;
		const translate = -visibleStart;
		setCameraTranslate((prev) => (Math.abs(prev - translate) > 0.5 ? translate : prev));
	}, [isCompactWidth, laneIndex, lanesCount]);

	useLayoutEffect(() => {
		updateCamera();
	}, [updateCamera]);

	useLayoutEffect(() => {
		if (!isCompactWidth) return;
		const node = trackRef.current;
		if (!node || typeof ResizeObserver === 'undefined') return;
		const ro = new ResizeObserver(() => updateCamera());
		ro.observe(node);
		return () => ro.disconnect();
	}, [isCompactWidth, updateCamera]);

	useEffect(() => {
		if (showStartScreen || gameStarted) return;
		moveToLane(laneIndex, true);
		updateCamera();
	}, [gameStarted, showStartScreen, laneIndex, moveToLane, updateCamera]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			e.preventDefault();
			if (isSpaceKey(e)) {
				handleSpaceKeyPress();
				return;
			}
			if (e.key === 'ArrowUp') {
				startForwardBoost();
				return;
			}
			if (e.key === 'ArrowDown') {
				startReverseBoost();
				return;
			}
			if (e.key === 'ArrowLeft') {
				moveCharacterLeft();
				return;
			}
			if (e.key === 'ArrowRight') {
				moveCharacterRight();
			}
		};
		const handleKeyUp = (e: KeyboardEvent) => {
			if (e.key === 'ArrowUp') {
				stopForwardBoost();
			}
			if (e.key === 'ArrowDown') {
				stopReverseBoost();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [
		handleSpaceKeyPress,
		startForwardBoost,
		startReverseBoost,
		stopForwardBoost,
		stopReverseBoost,
		moveCharacterLeft,
		moveCharacterRight,
	]);

	useEffect(() => {
		if (answersAtBottom) {
			setArrowUpHeld(false);
			setArrowDownHeld(false);
			setBoosting(false);
			applyMotion(NORMAL_SCALE, 1);
			swipeActiveDirectionRef.current = null;
			if (gameStarted) {
				clearActionHintTimer();
				actionHintTimerRef.current = window.setTimeout(() => {
					setShowActionHint(true);
				}, 2000);
			}
		} else {
			setShowActionHint(false);
			clearActionHintTimer();
		}
	}, [answersAtBottom, applyMotion, gameStarted, clearActionHintTimer]);

	useEffect(() => {
		if (!gameStarted) {
			setArrowUpHeld(false);
			setArrowDownHeld(false);
			setBoosting(false);
			applyMotion(NORMAL_SCALE, 1);
			swipeActiveDirectionRef.current = null;
		}
	}, [gameStarted, applyMotion]);

	const SWIPE_FORWARD_ACTIVATION = 30;
	const SWIPE_REVERSE_ACTIVATION = 80;

	const swipeHandlers = useSwipeable({
		onSwiping: (data) => {
			if (data.dir === 'Left') {
				preventMobileHistoryNavigation(data.event);
			}
			if (!prefersSwipe) return;
			if (data.dir === 'Up' && data.absY > SWIPE_FORWARD_ACTIVATION) {
				if (swipeActiveDirectionRef.current !== 'forward') {
					if (arrowDownHeldRef.current) stopReverseBoost();
					swipeActiveDirectionRef.current = 'forward';
					startForwardBoost();
				}
				return;
			}
			if (
				data.dir === 'Down' &&
				data.absY > SWIPE_REVERSE_ACTIVATION &&
				data.velocity < 0.6
			) {
				if (swipeActiveDirectionRef.current !== 'reverse') {
					if (arrowUpHeldRef.current) stopForwardBoost();
					swipeActiveDirectionRef.current = 'reverse';
					startReverseBoost();
				}
			}
		},
		onTap: () => {
			if (!prefersSwipe) return;
			handleSpaceKeyPress();
			// Advance instructions if active
			if (!gameStarted && !showStartScreen) {
				keyInstructionsRef.current?.advanceOnTap();
			}
		},
		onSwiped: (data) => {
			if (data.dir === 'Left') {
				preventMobileHistoryNavigation(data.event);
			}
			if (!prefersSwipe) return;
			if (swipeActiveDirectionRef.current === 'reverse') {
				swipeActiveDirectionRef.current = null;
				stopReverseBoost();
				return;
			}
			if (swipeActiveDirectionRef.current === 'forward') {
				swipeActiveDirectionRef.current = null;
				stopForwardBoost();
				// Advance instructions if active
				if (!gameStarted && !showStartScreen) {
					keyInstructionsRef.current?.advanceOnSwipeUp();
				}
				return;
			}
			if (data.dir === 'Left') {
				moveCharacterLeft();
				// Advance instructions if active
				if (!gameStarted && !showStartScreen) {
					keyInstructionsRef.current?.advanceOnSwipeLeft();
				}
				return;
			}
			if (data.dir === 'Right') {
				moveCharacterRight();
				// Advance instructions if active
				if (!gameStarted && !showStartScreen) {
					keyInstructionsRef.current?.advanceOnSwipeRight();
				}
			}
		},
		trackTouch: prefersSwipe,
		trackMouse: false,
		preventScrollOnSwipe: true,
		touchEventOptions: { passive: false },
	});

	useEffect(() => {
		if (!prefersSwipe) {
			swipeActiveDirectionRef.current = null;
			if (arrowUpHeldRef.current) stopForwardBoost();
			if (arrowDownHeldRef.current) stopReverseBoost();
			arrowUpHeldRef.current = false;
			arrowDownHeldRef.current = false;
		}
	}, [prefersSwipe, stopForwardBoost, stopReverseBoost]);

	useEffect(() => {
		if (showStartScreen || endOutcome) {
			stopGameMusic();
			return;
		}
		playGameMusic();
		return () => {
			stopGameMusic();
		};
	}, [endOutcome, showStartScreen, playGameMusic, stopGameMusic]);

	// Ensure character snaps to the chosen lane right after the start screen closes
	useEffect(() => {
		if (!showStartScreen) {
			moveToLane(laneIndex, true);
			const node = containerRef.current;
			if (node) {
				setGameVisible(true);
				gsap.fromTo(
					node,
					{ opacity: 0 },
					{ opacity: 1, duration: 0.35, ease: 'power1.out' },
				);
			} else {
				setGameVisible(true);
			}
		}
	}, [showStartScreen]);

	// Reset bottom flag so Space behaves for the new question and timeline replays
	useEffect(() => {
		answersAlreadyAtBottomRef.current = false;
		hasConfirmedCurrentQuestionRef.current = false;
		setAnswersAtBottom(false);
		setCelebratingCorrect(false);
		lastConfirmedWasCorrectRef.current = null;
		setQuestionStartTs(Date.now());

		const el = questionContainerRef.current;
		if (el && isQuestionTransitioningRef.current) {
			gsap.set(el, { opacity: 0 });
			gsap.to(el, {
				opacity: 1,
				duration: 0.35,
				ease: 'power1.inOut',
				onComplete: () => {
					isQuestionTransitioningRef.current = false;
				},
			});
		}
	}, [currentQuestionIndex]);

	useEffect(() => {
		return () => {
			clearActionHintTimer();
		};
	}, [clearActionHintTimer]);

	const handleShowNextQuestion = useCallback(() => {
		if (isQuestionTransitioningRef.current) return; // guard against double-trigger
		if (currentQuestionIndex >= questions.length - 1) {
			setEndOutcome(EndOutcome.SUCCEEDED);
			setGameStarted(false);
			return;
		}
		const el = questionContainerRef.current;
		if (el) {
			isQuestionTransitioningRef.current = true;
			gsap.to(el, {
				opacity: 0,
				duration: 0.25,
				ease: 'power1.inOut',
				onComplete: () => {
					setCurrentQuestionIndex((prev) =>
						Math.min(prev + 1, Math.max(0, questions.length - 1)),
					);
					setConfirmedIndex(null);
					lastConfirmedWasCorrectRef.current = null;
					gsap.set(qRef.current, { zIndex: 4 });
				},
			});
		} else {
			setCurrentQuestionIndex((prev) =>
				Math.min(prev + 1, Math.max(0, questions.length - 1)),
			);
			setConfirmedIndex(null);
			lastConfirmedWasCorrectRef.current = null;
			gsap.set(qRef.current, { zIndex: 4 });
		}
	}, [currentQuestionIndex, questions.length]);

	const onClose = useContext(PlaylistExitContext);
	const handleBack = useCallback(() => {
		if (onClose) {
			onClose();
			return;
		}
		if (freePlayCategoryPath) {
			historyPush(freePlayCategoryPath);
		} else {
			historyPush(FRONTEND_ROUTES.HOME);
		}
	}, [onClose, freePlayCategoryPath]);

	if (!gameStarted && showStartScreen) {
		return (
			<StartScreen
				onStart={() => {
					setShowStartScreen(false);
				}}
				onBack={handleBack}
			/>
		);
	}

	if (endOutcome) {
		return (
			<EndScreen outcome={endOutcome} hasAnswered={answers.length > 0} onClose={handleBack} />
		);
	}

	return (
		<div
			className={`lane-chooser lane-chooser--${theme.key}`}
			style={{ opacity: gameVisible ? 1 : 0 }}
			ref={containerRef}
			tabIndex={-1}
		>
			<Progress
				total={questions.length}
				currentIndex={currentQuestionIndex}
				answers={answers}
				totalPlayTime={totalSeconds}
				gameStarted={gameStarted}
				timeAdjustSeconds={timeAdjustSeconds}
				onTimeOut={() => {
					if (!endOutcome) {
						setEndOutcome(EndOutcome.LOST);
						setGameStarted(false);
					}
				}}
				onBack={handleBack}
			/>

			<Sky />

			<QuestionPanel
				key={currentQuestionKey}
				title={titleText}
				questionHtml={questionHtml}
				imageUrls={imageUrls}
				questionHasImage={questionHasImage}
				visible={gameStarted}
				onImageClick={handleClickQuestionImage}
				ref={questionContainerRef}
			/>

			<div className="lane-chooser__track" ref={trackRef} style={trackStyle}>
				<div className="lane-chooser__top" />

				<div
					className="lane-chooser__bottom"
					role="button"
					aria-label="Boost Down"
					tabIndex={0}
				>
					<LaneLayer
						ref={laneLayerRef}
						laneLinesCount={lanesCount}
						topWidthPct={getLaneTopPct()}
						bottomWidthPct={getLaneBottomPct()}
					/>
					<SideElements
						topWidthPct={getLaneTopPct()}
						bottomWidthPct={getLaneBottomPct()}
						side="both"
						motionScale={motionScale}
						motionDirection={motionDirection}
						bottomTargetY={bottomTargetY}
						paused={gameStarted && answersAtBottom && !jumping}
					/>
					<AnswersPanel
						topWidthPct={getLaneTopPct()}
						lanesCount={lanesCount}
						labels={answerLabels}
						confirmedIndex={confirmedIndex}
						answerCorrectFlags={answerCorrectFlags}
						ref={answersRef}
						laneLayerRef={laneLayerRef}
						characterRef={qRef}
						answersAtBottom={answersAtBottom}
						resetKey={currentQuestionIndex}
						pausedInitially={!gameStarted}
						gameStarted={gameStarted}
						motionScale={motionScale}
						motionDirection={motionDirection}
						onShowNextQuestion={handleShowNextQuestion}
						onBottomTargetComputed={setBottomTargetY}
						onAnswersAtBottomChange={setAnswersAtBottom}
						deltaSecondsForConfirmed={lastDeltaSeconds}
					/>
					<Character
						ref={qRef}
						jumping={jumping}
						celebratingCorrect={celebratingCorrect}
						showStatic={answersAtBottom}
						boosting={boosting}
					/>
				</div>
			</div>

			{!gameStarted && countdownDisplay !== null && (
				<div className="lane-chooser__countdown">
					<div className="lane-chooser__countdown-number">{countdownDisplay}</div>
				</div>
			)}

			{prefersSwipe && (
				<div className="lane-chooser__swipe-zone" aria-hidden {...swipeHandlers} />
			)}

			{gameStarted && showActionHint && (
				<KeyInstructions initialStage="space" enableKeys={false} showSkip={false} />
			)}

			{!gameStarted && !showStartScreen && (
				<KeyInstructions
					ref={keyInstructionsRef}
					enableKeys={true}
					onComplete={() => {
						startCountdown();
					}}
				/>
			)}
		</div>
	);
};

const LaneChooserGame = (props: LaneChooserGameProps) => {
	const themeKey = props.playList?.free_play_theme;
	return (
		<LaneChooserThemeProvider themeKey={themeKey}>
			<LaneChooserGameInner {...props} />
		</LaneChooserThemeProvider>
	);
};

function mapStateToProps({ playlistReducer }: any) {
	return {
		playList: playlistReducer.playList,
		questions: playlistReducer.questions,
		isBridgePlaylist: playlistReducer.isBridgePlaylist,
	};
}

const mapDispatchToProps = {
	sendAnswerRequest: playlistActions.sendAnswerRequest,
	finishPlaylistRequest: playlistActions.finishPlaylistRequest,
	setImagePreviewModal: imagePreviewActions.setImagePreviewModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(LaneChooserGame);
